import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'

import {
  Badge,
  Button,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
} from '../../components/ui'
import RedshiftDataSourcePanel from '../revenue-close/RedshiftDataSourcePanel'
import {
  downloadSubledgerExport,
  useSubledgerAgentReview,
  useSubledgerAvailableConnectors,
  useSubledgerDataSourceConfig,
  useSubledgerHealth,
  useSubledgerRun,
  useClearSubledgerDataSource,
  useSetSubledgerDataSource,
  useTestSubledgerDataSource,
} from './api'
import type {
  AgentReview,
  ExportStatus,
  ReconcilingItem,
  SubledgerHealth,
  SubledgerRun,
  SubledgerView,
  TieOut,
} from './types'
import { formatCurrency, formatPercent, hitlBadgeClass, priorityBadgeClass, severityBadgeClass } from './utils'

const DEFAULT_REDSHIFT_SCHEMA = 'subledger_revenue_reconciler'
const EXPECTED_REDSHIFT_TABLES = [
  'subledger_revenue_runs',
  'subledger_revenue_tieouts',
  'subledger_revenue_reconciling_items',
  'subledger_revenue_journal_rows',
  'subledger_revenue_audit_trail',
  'subledger_revenue_source_files',
]

export default function SubledgerRevenueReconcilerPage({ view = 'overview' }: { view?: SubledgerView }) {
  const { data, isLoading, error } = useSubledgerRun()
  const { data: health } = useSubledgerHealth()

  if (view === 'sources') {
    return (
      <SourcesView
        run={data}
        health={health}
        isRunLoading={isLoading}
        hasRunError={Boolean(error)}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        Loading sub-ledger revenue reconciliation...
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="mx-auto w-full min-w-0 max-w-[1180px] space-y-4">
        <DataSourceStrip health={health} />
        <SubledgerDataSourceConfiguration health={health} />
        <Panel>
          <PanelBody>
            <p className="text-sm text-rose-600 dark:text-rose-400">
              Sub-ledger reconciliation data could not be loaded. Check the Close Calendar gate for blocking prerequisites.
            </p>
          </PanelBody>
        </Panel>
      </div>
    )
  }

  if (view === 'review') return <ReviewView run={data} health={health} />
  if (view === 'exports') return <ExportsView run={data} health={health} />
  return <OverviewView run={data} health={health} />
}

function SubledgerDataSourceConfiguration({ health }: { health?: SubledgerHealth }) {
  const { data: config, isLoading: configLoading } = useSubledgerDataSourceConfig()
  const { data: connectors = [], isLoading: connectorsLoading } = useSubledgerAvailableConnectors()
  const saveMutation = useSetSubledgerDataSource()
  const clearMutation = useClearSubledgerDataSource()
  const testMutation = useTestSubledgerDataSource()

  return (
    <RedshiftDataSourcePanel
      appletName="Sub-Ledger Revenue Reconciler"
      defaultSchema={DEFAULT_REDSHIFT_SCHEMA}
      expectedTables={EXPECTED_REDSHIFT_TABLES}
      config={config}
      health={health}
      connectors={connectors}
      isLoading={configLoading || connectorsLoading}
      saveMutation={saveMutation}
      clearMutation={clearMutation}
      testMutation={testMutation}
    />
  )
}

function OverviewView({ run, health }: { run: SubledgerRun; health?: SubledgerHealth }) {
  return (
    <div className="mx-auto w-full min-w-0 max-w-[1500px] space-y-6">
      <DataSourceStrip health={health} />
      <DecisionStrip run={run} />

      <div className="grid grid-cols-[minmax(0,1fr)_420px] gap-6 max-xl:grid-cols-1">
        <TieOutTable rows={run.tieouts} />
        <ReviewRail items={run.reconciling_items} />
      </div>

      <div className="grid grid-cols-2 gap-6 max-2xl:grid-cols-1">
        <JournalTable rows={run.journal_entries} />
        <AuditTable rows={run.audit_trail.slice(0, 8)} />
      </div>
    </div>
  )
}

function DataSourceStrip({ health }: { health?: SubledgerHealth }) {
  const sourceMode = health?.data_source_mode ?? health?.source_mode ?? 'unconfigured'
  let sourceLabel = sourceMode
  if (health?.redshift_connected) {
    sourceLabel = 'Redshift connected'
  } else if (sourceMode === 'unconfigured') {
    sourceLabel = 'Redshift required'
  }
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 dark:border-dark-border dark:bg-dark-surface sm:px-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={health?.status === 'ok' ? 'success' : 'warning'}>
            Close package {health?.status === 'ok' ? 'available' : 'checking'}
          </Badge>
          <Badge variant={health?.redshift_connected ? 'success' : 'warning'}>
            {sourceLabel}
          </Badge>
          {health?.redshift_schema && (
            <Badge variant="secondary" className="inline-block max-w-full whitespace-normal break-all">
              schema {health.redshift_schema}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <Badge variant={health?.langfuse_enabled ? 'success' : 'warning'}>
            Langfuse {health?.langfuse_enabled ? 'enabled' : 'inactive'}
          </Badge>
          <Badge variant="info">RR-04 tie-out</Badge>
        </div>
      </div>
      {health?.message && (
        <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
          {health.message}
        </p>
      )}
    </div>
  )
}

function DecisionStrip({ run }: { run: SubledgerRun }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1.3fr_0.7fr_0.7fr_0.7fr]">
        <div className="border-b border-gray-200 p-4 dark:border-dark-border sm:col-span-2 sm:p-5 xl:col-span-1 xl:border-b-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Tie-out decision
          </div>
          <div className="mt-2 flex flex-wrap items-end gap-x-4 gap-y-2">
            <div className="text-2xl font-semibold tracking-normal text-charcoal dark:text-gray-100 sm:text-3xl">
              {formatCurrency(run.summary.net_variance, true)}
            </div>
            <Badge variant="warning">{run.summary.pending_review_count} review items</Badge>
          </div>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
            {run.summary.policy} controls are ready once orphan GL, misclassification, and cutoff items are disposed.
          </p>
        </div>
        <DecisionMetric label="Subledger" value={formatCurrency(run.summary.subledger_posted_total, true)} />
        <DecisionMetric label="GL credits" value={formatCurrency(run.summary.gl_credit_total, true)} />
        <DecisionMetric label="Corrective JE" value={run.summary.journal_balanced ? 'Balanced' : 'Blocked'} />
      </div>
    </div>
  )
}

function DecisionMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-t border-gray-200 p-4 dark:border-dark-border sm:p-5 xl:border-l xl:border-t-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-2 break-words text-xl font-semibold text-charcoal dark:text-gray-100">{value}</div>
    </div>
  )
}

function TieOutTable({ rows }: { rows: TieOut[] }) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader>
        <PanelTitle>Subledger to GL tie-out</PanelTitle>
        <PanelDescription>
          Revenue account variance, exception routing, and auto-post eligibility.
        </PanelDescription>
      </PanelHeader>
      <div className="overflow-x-auto">
        <table className="min-w-[820px] text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <th className="px-5 py-3 font-semibold">Account</th>
              <th className="px-4 py-3 text-right font-semibold">Subledger</th>
              <th className="px-4 py-3 text-right font-semibold">GL</th>
              <th className="px-4 py-3 text-right font-semibold">Variance</th>
              <th className="px-4 py-3 font-semibold">Disposition</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
            {rows.map(row => (
              <tr key={row.tieout_id} className="align-top">
                <td className="px-5 py-4">
                  <div className="font-semibold text-charcoal dark:text-gray-100">
                    {row.revenue_account} - {row.account_name}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {row.exception_type || 'Clean tie-out'} - {row.reviewer}
                  </div>
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.subledger_posted)}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.gl_credit)}
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="font-semibold tabular-nums text-charcoal dark:text-gray-100">
                    {formatCurrency(row.variance)}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {formatPercent(row.variance_pct)}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className={hitlBadgeClass(row.hitl_state)}>{row.disposition}</span>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {row.reconciling_items_count} review / {row.auto_aware_items_count} aware
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function ReviewRail({ items }: { items: ReconcilingItem[] }) {
  const reviewItems = items.filter(item => item.hitl_state === 'PENDING_REVIEW')
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Controller review queue</PanelTitle>
        <PanelDescription>
          Reconciling items requiring disposition before revenue close.
        </PanelDescription>
      </PanelHeader>
      <PanelBody className="space-y-3">
        {reviewItems.map(item => (
          <div key={item.recon_id} className="rounded-lg border border-gray-200 p-4 dark:border-dark-border">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-charcoal dark:text-gray-100">
                  {item.recon_id} - {item.exception_type}
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {item.revenue_account} - {item.reviewer}
                </div>
              </div>
              <span className={priorityBadgeClass(item.priority)}>{item.priority}</span>
            </div>
            <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {item.description}
            </p>
            <div className="mt-3 text-sm font-semibold tabular-nums text-charcoal dark:text-gray-100">
              {formatCurrency(item.amount)}
            </div>
          </div>
        ))}
      </PanelBody>
    </Panel>
  )
}

function JournalTable({ rows }: { rows: SubledgerRun['journal_entries'] }) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader>
        <PanelTitle>Corrective journal</PanelTitle>
        <PanelDescription>
          NetSuite-ready rows for R2, R3, and R4 review items.
        </PanelDescription>
      </PanelHeader>
      <div className="overflow-x-auto">
        <table className="min-w-[680px] text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <th className="px-5 py-3 font-semibold">Reference</th>
              <th className="px-4 py-3 font-semibold">Account</th>
              <th className="px-4 py-3 text-right font-semibold">Debit</th>
              <th className="px-4 py-3 text-right font-semibold">Credit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
            {rows.map(row => (
              <tr key={`${row.je_external_id}-${row.line}`}>
                <td className="px-5 py-4">
                  <div className="font-medium text-charcoal dark:text-gray-100">{row.reference}</div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{row.memo}</div>
                </td>
                <td className="px-4 py-4 text-gray-600 dark:text-gray-300">
                  {row.account}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.debit)}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.credit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function AuditTable({ rows }: { rows: SubledgerRun['audit_trail'] }) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Audit trail sample</PanelTitle>
        <PanelDescription>
          Auto-posted and review-driven evidence retained for RR-04.
        </PanelDescription>
      </PanelHeader>
      <PanelBody className="space-y-3">
        {rows.map(row => (
          <div key={row.audit_id} className="rounded-lg border border-gray-200 p-4 dark:border-dark-border">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-semibold text-charcoal dark:text-gray-100">{row.audit_id}</div>
              <span className={hitlBadgeClass(row.hitl_state)}>{row.hitl_state}</span>
            </div>
            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {row.rule_or_trigger}
            </p>
          </div>
        ))}
      </PanelBody>
    </Panel>
  )
}

function ReviewView({ run, health }: { run: SubledgerRun; health?: SubledgerHealth }) {
  const reviewMutation = useSubledgerAgentReview()
  const review = reviewMutation.data

  return (
    <div className="mx-auto w-full min-w-0 max-w-[1300px] space-y-6">
      <DataSourceStrip health={health} />
      <Panel>
        <PanelHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <PanelTitle>Agent review</PanelTitle>
              <PanelDescription>
                Deterministic EC-03e review across orphan credits, account mapping, cutoff, and EC-03d awareness.
              </PanelDescription>
            </div>
            <Button
              onClick={() => reviewMutation.mutate({ use_llm: false })}
              disabled={reviewMutation.isPending}
            >
              <ShieldCheckIcon className="mr-2 h-4 w-4" />
              Run Review
            </Button>
          </div>
        </PanelHeader>
        <PanelBody>
          {review ? <AgentReviewResult review={review} /> : (
            <div className="rounded-lg border border-dashed border-gray-300 p-5 text-sm text-gray-500 dark:border-dark-border dark:text-gray-400">
              Run the review to generate a Controller checklist for {run.summary.pending_review_count} reconciling item(s).
            </div>
          )}
        </PanelBody>
      </Panel>
      <ReviewRail items={run.reconciling_items} />
    </div>
  )
}

function AgentReviewResult({ review }: { review: AgentReview }) {
  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{review.mode}</Badge>
          <Badge variant="info">{review.agent_name}</Badge>
          <Badge variant={review.traced_with_langfuse ? 'success' : 'warning'}>
            Langfuse {review.traced_with_langfuse ? 'traced' : 'inactive'}
          </Badge>
          {review.confidence != null && (
            <Badge variant="secondary">{Math.round(review.confidence * 100)}% confidence</Badge>
          )}
        </div>
        <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
          {review.summary}
        </p>
      </div>
      <div className="grid gap-3">
        {review.findings.map(finding => (
          <div key={`${finding.title}-${finding.recon_id}`} className="rounded-lg border border-gray-200 p-4 dark:border-dark-border">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-semibold text-charcoal dark:text-gray-100">{finding.title}</div>
              <span className={severityBadgeClass(finding.severity)}>{finding.severity}</span>
            </div>
            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {finding.detail}
            </p>
          </div>
        ))}
      </div>
      <div>
        <div className="text-sm font-semibold text-charcoal dark:text-gray-100">Recommended actions</div>
        <ul className="mt-2 space-y-2 text-sm text-gray-600 dark:text-gray-300">
          {review.recommended_actions.map(action => (
            <li key={action} className="flex gap-2">
              <CheckCircleIcon className="mt-0.5 h-4 w-4 flex-none text-emerald-600" />
              <span>{action}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function SourcesView({
  run,
  health,
  isRunLoading = false,
  hasRunError = false,
}: {
  run?: SubledgerRun
  health?: SubledgerHealth
  isRunLoading?: boolean
  hasRunError?: boolean
}) {
  return (
    <div className="mx-auto w-full min-w-0 max-w-[1200px] space-y-6">
      <DataSourceStrip health={health} />
      <SubledgerDataSourceConfiguration health={health} />
      <Panel>
        <PanelHeader>
          <PanelTitle>Source evidence</PanelTitle>
          <PanelDescription>
            NetSuite ARM, GL, SFDC, upstream EC-03d, and prior-period archive evidence.
          </PanelDescription>
        </PanelHeader>
        <PanelBody className="grid gap-3">
          {isRunLoading ? (
            <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-500 dark:border-dark-border dark:text-gray-400">
              Loading sub-ledger source evidence...
            </div>
          ) : hasRunError || !run ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
              Sub-ledger source evidence could not be loaded. The Redshift connector can still be reviewed above.
            </div>
          ) : (
            <>
              {run.source_files.map(file => (
                <div key={file.filename} className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-gray-200 p-4 dark:border-dark-border">
                  <div>
                    <div className="font-semibold text-charcoal dark:text-gray-100">{file.filename}</div>
                    <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{file.description}</div>
                  </div>
                  {file.available ? (
                    <Badge variant="success">Available</Badge>
                  ) : (
                    <Badge variant="danger">Missing</Badge>
                  )}
                </div>
              ))}
              <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
                <div className="text-sm font-semibold text-charcoal dark:text-gray-100">Source tables</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {run.data_source.tables.map(table => (
                    <Badge key={table} variant="secondary">{table}</Badge>
                  ))}
                </div>
              </div>
            </>
          )}
        </PanelBody>
      </Panel>
    </div>
  )
}

function ExportsView({ run, health }: { run: SubledgerRun; health?: SubledgerHealth }) {
  return (
    <div className="mx-auto w-full min-w-0 max-w-[1200px] space-y-6">
      <DataSourceStrip health={health} />
      <Panel>
        <PanelHeader>
          <PanelTitle>NetSuite exports</PanelTitle>
          <PanelDescription>
            Tie-out, reconciling item, balanced journal, and audit trail files for EC-03e.
          </PanelDescription>
        </PanelHeader>
        <PanelBody className="grid gap-4 md:grid-cols-2">
          <ExportCard
            title="Subledger GL tie-out"
            status={run.exports.tieout}
            onDownload={() => downloadSubledgerExport('tieout')}
          />
          <ExportCard
            title="Reconciling items"
            status={run.exports.reconciling_items}
            onDownload={() => downloadSubledgerExport('reconciling_items')}
          />
          <ExportCard
            title="NetSuite journal import"
            status={run.exports.journal_entry}
            onDownload={() => downloadSubledgerExport('journal')}
          />
          <ExportCard
            title="Audit trail"
            status={run.exports.audit_trail}
            onDownload={() => downloadSubledgerExport('audit')}
          />
        </PanelBody>
      </Panel>
    </div>
  )
}

function ExportCard({ title, status, onDownload }: { title: string; status: ExportStatus; onDownload: () => void }) {
  return (
    <div className="rounded-lg border border-gray-200 p-5 dark:border-dark-border">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold text-charcoal dark:text-gray-100">{title}</div>
          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{status.filename}</div>
        </div>
        {status.balanced ? (
          <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
        ) : (
          <XCircleIcon className="h-5 w-5 text-rose-600" />
        )}
      </div>
      <div className="mt-5 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
        <ExportMetric label="Rows" value={String(status.rows)} />
        <ExportMetric label="Debits" value={formatCurrency(status.debit_total, true)} />
        <ExportMetric label="Credits" value={formatCurrency(status.credit_total, true)} />
      </div>
      <Button className="mt-5 w-full" variant="secondary" onClick={onDownload}>
        <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
        Download CSV
      </Button>
    </div>
  )
}

function ExportMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 font-semibold text-charcoal dark:text-gray-100">{value}</div>
    </div>
  )
}
